package org.zq.astrock_ai.ui.research.gushi.components

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import org.zq.astrock_ai.ui.research.gushi.StockColors
import org.zq.astrock_ai.ui.research.gushi.adaptiveDialogWidth
import org.zq.astrock_ai.ui.research.gushi.data.PortfolioSummary
import org.zq.astrock_ai.util.formatDecimal

@Composable
fun PortfolioBoardDialog(
    summary: PortfolioSummary,
    onDismiss: () -> Unit,
    onSelectCode: (String) -> Unit = {},
    onExport: () -> Unit = {},
) {
    val shape = RoundedCornerShape(14.dp)
    val pnlColor = when {
        (summary.totalPnl ?: 0.0) > 0 -> StockColors.UpRed
        (summary.totalPnl ?: 0.0) < 0 -> StockColors.DownGreen
        else -> StockColors.TextSecondary
    }
    Dialog(onDismissRequest = onDismiss) {
        Column(
            modifier = Modifier
                .adaptiveDialogWidth(420.dp)
                .heightIn(max = 560.dp)
                .background(Color.White, shape)
                .border(BorderStroke(1.dp, StockColors.Border), shape)
                .padding(18.dp),
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "组合盈亏",
                    color = StockColors.TextPrimary,
                    fontWeight = FontWeight.Bold,
                    fontSize = 18.sp,
                    modifier = Modifier.weight(1f),
                )
                Text(
                    "导出",
                    color = StockColors.PrimaryBlue,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier
                        .background(Color(0xFFE8F0FE), RoundedCornerShape(12.dp))
                        .clickable(onClick = onExport)
                        .padding(horizontal = 10.dp, vertical = 5.dp),
                )
                Spacer(Modifier.width(8.dp))
                Text(
                    "关闭",
                    color = StockColors.TextSecondary,
                    fontSize = 13.sp,
                    modifier = Modifier
                        .clickable(onClick = onDismiss)
                        .padding(horizontal = 6.dp, vertical = 5.dp),
                )
            }
            Spacer(Modifier.height(10.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .background(Color(0xFFF8F9FB), RoundedCornerShape(10.dp))
                    .padding(12.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                SummaryCell("成本", formatDecimal(summary.totalCost), Modifier.weight(1f))
                SummaryCell(
                    "市值",
                    summary.totalMarket?.let { formatDecimal(it) } ?: "--",
                    Modifier.weight(1f),
                )
                SummaryCell(
                    "盈亏",
                    summary.totalPnl?.let { formatDecimal(it) } ?: "--",
                    Modifier.weight(1f),
                    valueColor = pnlColor,
                )
                SummaryCell(
                    "盈亏%",
                    summary.totalPnlPct?.let { "${formatDecimal(it)}%" } ?: "--",
                    Modifier.weight(1f),
                    valueColor = pnlColor,
                )
            }
            Spacer(Modifier.height(12.dp))
            if (summary.rows.isEmpty()) {
                Text("暂无持仓，先在研究台添加持仓", color = StockColors.TextSecondary, fontSize = 13.sp)
            } else {
                LazyColumn(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    items(summary.rows, key = { it.code }) { row ->
                        val color = when {
                            (row.pnl ?: 0.0) > 0 -> StockColors.UpRed
                            (row.pnl ?: 0.0) < 0 -> StockColors.DownGreen
                            else -> StockColors.TextSecondary
                        }
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .background(Color(0xFFF8F9FB), RoundedCornerShape(10.dp))
                                .clickable { onSelectCode(row.code) }
                                .padding(12.dp),
                        ) {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Text(
                                    row.name,
                                    color = StockColors.TextPrimary,
                                    fontWeight = FontWeight.SemiBold,
                                    fontSize = 14.sp,
                                    modifier = Modifier.weight(1f),
                                )
                                Text(
                                    row.pnlPct?.let { "${if (it >= 0) "+" else ""}${formatDecimal(it)}%" } ?: "--",
                                    color = color,
                                    fontWeight = FontWeight.Bold,
                                    fontSize = 14.sp,
                                )
                            }
                            Spacer(Modifier.height(4.dp))
                            Text(
                                "${row.code} · 成本 ${formatDecimal(row.costPrice)} × ${row.quantity}" +
                                    " · 现价 ${row.lastPrice?.let { formatDecimal(it) } ?: "--"}",
                                color = StockColors.TextSecondary,
                                fontSize = 11.sp,
                            )
                            Text(
                                "盈亏 ${row.pnl?.let { formatDecimal(it) } ?: "--"} · 市值 ${row.marketValue?.let { formatDecimal(it) } ?: "--"}",
                                color = color,
                                fontSize = 11.sp,
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun SummaryCell(
    label: String,
    value: String,
    modifier: Modifier = Modifier,
    valueColor: Color = StockColors.TextPrimary,
) {
    Column(modifier = modifier, horizontalAlignment = Alignment.CenterHorizontally) {
        Text(value, color = valueColor, fontWeight = FontWeight.Bold, fontSize = 13.sp)
        Text(label, color = StockColors.TextSecondary, fontSize = 10.sp)
    }
}
